import { useCallback, useMemo } from "react";
import { useContextQueueProcess } from "@/app/module/queue-process/index";
import {
  QueueProcessValueType,
  QueueProcessItemType,
  QueueProcessUpdateDataType,
} from "@/app/module/queue-process/types";

export type QueueProcessValueOptionalType<
  DataType = any,
  ResultType = any,
> = Omit<QueueProcessValueType<DataType, ResultType>, "key"> & {
  key?: string;
};

export function useQueueProcess<DataType = any, ResultType = any>(
  processKey?: QueueProcessItemType["key"],
) {
  const { addProcess, findProcessById, updateQueueProcessById, processItems } =
    useContextQueueProcess();

  const addQueueProcess = useCallback(
    (data: QueueProcessValueOptionalType<DataType, ResultType>) => {
      const key = data.key || processKey;

      if (!key) {
        throw new Error("Process key is required");
      }

      addProcess<DataType, ResultType>({
        ...data,
        key,
      });
    },
    [addProcess, processKey],
  );

  const updateCurrentProcess = useCallback(
    (data: QueueProcessUpdateDataType<DataType, ResultType>) =>
      !!processKey &&
      updateQueueProcessById<DataType, ResultType>(processKey, data),
    [updateQueueProcessById, processKey],
  );

  const currentProcess: QueueProcessItemType<DataType, ResultType> | undefined =
    useMemo(() => {
      return processKey ? findProcessById(processKey) : undefined;
    }, [processItems, processKey]);

  return {
    addQueueProcess,
    updateCurrentProcess,
    currentProcess,
  };
}
